import Grid from "@mui/material/Grid";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import DashboardLayout from "custom/LayoutContainers/DashboardLayout";
import {useCallback, useEffect, useState} from "react";
import {createAdmin, deleteAdmin, getAllAdmins, updateAdmin} from "../../api/admin/adminApiClient";
import DeleteConfirmDialog from "./components/DeleteConfirmDialog";
import UserFormDialog from "../../components/UserFormDialog";
import SoftButton from "components/SoftButton";
import SoftInput from "../../components/SoftInput";
import {Outlet, useLocation, useNavigate} from "react-router-dom";
import {IconButton} from "@mui/material";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {Delete as DeleteIcon, Edit as EditIcon, Visibility as VisibilityIcon} from "@mui/icons-material";
import EnhancedTable from "../../components/EnhancedTable";
import DataTable, {renderBase64ImageCell} from "../../components/DataTable";
import {handleApiError, showDeleteSuccess} from "../../utils/notificationUtils";

function Overview() {
    const [admins, setAdmins] = useState([]);
    const [filteredAdmins, setFilteredAdmins] = useState([]);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [adminToDelete, setAdminToDelete] = useState(null);
    const [adminToEdit, setAdminToEdit] = useState(null);
    const [editAddDialogOpen, setEditAddDialogOpen] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();

    const isOnChildRoute = location.pathname.includes('/admins/') && location.pathname !== '/admins';

    const columns = [
        {
            name: "profile Pic",
            value: "profilePic",
            align: "center",
            width: "10",
            renderCell: (row) => renderBase64ImageCell(row, "profilePic"),
        },
        {name: "first Name", value: "firstName", align: "left", width: "10"},
        {name: "Last Name", value: "lastName", align: "left", width: "10"},
        {name: "email", value: "email", align: "left", width: "20"},
        {name: "phone No", value: "phoneNumber", align: "left", width: "10"},
        {name: "position", value: "userRole", align: "left", width: "10"},
        {name: "status", value: "status", align: "left", width: "10"},
        {
            name: "Actions",
            value: "action",
            align: "center",
            width: "10",
            renderCell: (row) => (
                <>
                    <IconButton color="primary" size="small" onClick={() => handleViewClick(row)}>
                        <VisibilityIcon/>
                    </IconButton>
                    <IconButton
                        color="error"
                        size="small"
                        onClick={() => handleDeleteClick(row)}
                        disabled={row.userRole === "SYSTEM_ADMIN"}
                    >
                        <DeleteIcon/>
                    </IconButton>
                    <IconButton
                        color="primary"
                        size="small"
                        onClick={() => handleEditClick(row)}
                        disabled={row.userRole === "SYSTEM_ADMIN"}
                    >
                        <EditIcon/>
                    </IconButton>
                </>
            ),
        },
    ];

    const loadAdmins = useCallback(async () => {
        try {
            setIsLoading(true);
            
            const response = await getAllAdmins();
            setAdmins(response.data);
            setFilteredAdmins(response.data);
        } catch (error) {
            console.error('Error loading admins:', error);
            handleApiError(error, 'Failed to load admins. Please try again.');
            setAdmins([]);
            setFilteredAdmins([]);
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        loadAdmins().then();
    }, [loadAdmins]);

    const handleAdminAdded = async (formData) => {
        try {
            await createAdmin(formData);
            await loadAdmins();
        } catch (error) {
            // Re-throw so the dialog can surface the error via handleApiError.
            throw error;
        }

    };

    const handleViewClick = (admin) => {
        navigate(`/admins/${admin.id}`);
    };

    const handleDeleteClick = (admin) => {
        setAdminToDelete(admin);
        setDeleteDialogOpen(true);
    };

    const handleDeleteConfirm = async () => {
        try {
            await deleteAdmin(adminToDelete.id);
            await loadAdmins();
            setDeleteDialogOpen(false);
            setAdminToDelete(null);
            showDeleteSuccess('Admin');
        } catch (err) {
            handleApiError(err, 'Failed to delete admin');
        }
    };

    const handleDeleteCancel = () => {
        setDeleteDialogOpen(false);
        setAdminToDelete(null);
    };

    const handleEditClick = (admin) => {
        setAdminToEdit(admin);
        setIsEdit(true);
        setEditAddDialogOpen(true);
    };

    const handleEditAdmin = async (formData) => {
        await updateAdmin(adminToEdit.id, formData);
        await loadAdmins();
        setEditAddDialogOpen(false);
        setAdminToEdit(null);
    };
    const handleOpenAddDialog = () => {
        setAdminToEdit(null);
        setIsEdit(false);
        setEditAddDialogOpen(true);
    }
    const handleCloseDialog = () => {
        setDeleteDialogOpen(false)
        setEditAddDialogOpen(false);
        setAdminToEdit(null);
        setAdminToDelete(null);
    }

    const handleFilterAdmin = (value) => {
        let filtered = admins.filter(admin => {
            return admin.firstName.toLowerCase().includes(value.toLowerCase()) ||
                admin.lastName.toLowerCase().includes(value.toLowerCase()) ||
                admin.email.toLowerCase().includes(value.toLowerCase()) ||
                admin.phoneNumber.toLowerCase().includes(value.toLowerCase());
        });
        setFilteredAdmins(filtered);
    }

    const handleOnSubmit = async (formData) => {
        try {
            if (isEdit) {
                await handleEditAdmin(formData);
            } else {
                await handleAdminAdded(formData);
            }
            setEditAddDialogOpen(false);
        } catch (error) {
            console.error('Error in handleOnSubmit:', error);
            // Re-throw so AddUserDialog can handle API errors via notifications
            throw error;
        }
    };

    return (
        <DashboardLayout>
            <SoftBox pt={2} px={2} width="100%">
                <SoftBox pt={2} px={2} display="flex" alignItems="center" justifyContent="space-between" width="100%" gap={2}>
                    <SoftBox display="flex" alignItems="center" gap={2} flexShrink={0}>
                        {isOnChildRoute && (
                            <IconButton
                                onClick={() => navigate('/admins')}
                                sx={{
                                    backgroundColor: 'primary.main',
                                    color: 'white',
                                    '&:hover': {
                                        backgroundColor: 'primary.dark',
                                    }
                                }}
                            >
                                <ArrowBackIcon/>
                            </IconButton>
                        )}
                        <SoftTypography variant="h6" fontWeight="medium">
                            {isOnChildRoute ? 'Admin Details' : 'Admins'}
                        </SoftTypography>
                    </SoftBox>
                    {!isOnChildRoute && (
                        <>
                            <SoftBox flexGrow={1} maxWidth={560}>
                                <SoftInput
                                    placeholder="Type here to filter by name, email or phone"
                                    icon={{component: "search", direction: "left"}}
                                    onChange={(event) => handleFilterAdmin(event.target.value)}
                                />
                            </SoftBox>
                            <SoftButton color="primary" onClick={handleOpenAddDialog} sx={{flexShrink: 0}}>
                                Add Admin
                            </SoftButton>
                        </>
                    )}
                </SoftBox>
            </SoftBox>
            <SoftBox p={2} width="100%">
                {isOnChildRoute ? (
                    <Outlet/>
                ) : (
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <EnhancedTable
                                TableComponent={DataTable}
                                isLoading={isLoading}
                                data={filteredAdmins}
                                loadingMessage="Loading admins..."
                                noDataMessage="No admins found"
                                columns={columns}
                            />
                        </Grid>
                    </Grid>
                )}
            </SoftBox>
            <DeleteConfirmDialog
                open={deleteDialogOpen}
                onClose={handleDeleteCancel}
                onDelete={handleDeleteConfirm}
                warningMessage={`Are you sure you want to delete ${adminToDelete?.firstName || ""} ${adminToDelete?.lastName || ""}? This action cannot be undone.`}
            />
            <UserFormDialog
                open={editAddDialogOpen}
                onClose={handleCloseDialog}
                onSubmit={handleOnSubmit}
                user={adminToEdit}
                isEdit={isEdit}
                entityLabel="Admin"
                defaultUserRole="ADMIN"
                minAge={18}
            />
        </DashboardLayout>
    );
}

export default Overview;
